import React from 'react';
import { useLanguage } from '../../context/LanguageContext';

/**
 * Category thresholds (raw value, e.g., ppm):
 * 0–100: Insufficient
 * 100–1000: Good
 * 1000–2000: Elevated
 * >2000: Harmful
 */
const RANGES = [
  { max: 100,    key: 'insufficient', translationKey: 'minerals_level_insufficient' },
  { max: 1000,   key: 'good',         translationKey: 'minerals_level_good' },
  { max: 2000,   key: 'elevated',     translationKey: 'minerals_level_elevated' },
  { max: Infinity, key: 'harmful',    translationKey: 'minerals_level_harmful' },
];

const STYLE_BY_KEY = {
  insufficient: { bg: '#EFF6FF', text: '#1D4ED8', border: '#BFDBFE' },
  good:         { bg: '#ECFDF5', text: '#047857', border: '#A7F3D0' },
  elevated:     { bg: '#FFFBEB', text: '#B45309', border: '#FDE68A' },
  harmful:      { bg: '#FEF2F2', text: '#B91C1C', border: '#FECACA' },
};

function getCategory(value) {
  if (value === -1 || value === undefined || value === null) return null;
  return RANGES.find(r => value <= r.max);
}

export default function MineralsLevelIndicator({
  value,
  className = '',
  showValue = true,
  unit = '',
}) {
  const { translations } = useLanguage();
  const category = getCategory(value);

  if (!category) {
    // No data case
    return (
      <span
        className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm ${className}`}
        style={{ backgroundColor: '#F3F4F6', color: '#374151', borderColor: '#E5E7EB' }}
        aria-label="No minerals data"
        title="No minerals data"
      >
        {translations?.no_measures || 'No data'}
      </span>
    );
  }

  const style = STYLE_BY_KEY[category.key];
  const label =
    translations?.[category.translationKey] ??
    // Safe English fallback if the key is missing:
    ({
      minerals_level_insufficient: 'Insufficient minerals level',
      minerals_level_good: 'Good minerals level',
      minerals_level_elevated: 'Elevated minerals level',
      minerals_level_harmful: 'Harmful minerals level',
    }[category.translationKey]);

  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-medium ${className}`}
      style={{ backgroundColor: style.bg, color: style.text, borderColor: style.border }}
      aria-label={label}
      title={label}
    >
      <span>{label}</span>
      {showValue && typeof value === 'number' && (
        <span style={{ opacity: 0.8 }}>· {value}{unit ? ` ${unit}` : ''}</span>
      )}
    </span>
  );
}
